import React, {useState, useImperativeHandle, forwardRef} from 'react';

import CountryDropdown from './CountryDropdown';
import FormField from './FormField';

export type BillingInfo = {
  country: string;
  locality: string;
  postalCode: string;
  address: string;
};

// Export the ref methods so parent components can access validation
export interface BillingAddressFormRef {
  validateAll: () => boolean;
  hasErrors: () => boolean;
  getErrors: () => Partial<Record<keyof BillingInfo, string>>;
}

const BillingAddressForm = forwardRef<
  BillingAddressFormRef,
  {
    billingInfo: BillingInfo;
    setBillingInfo: React.Dispatch<React.SetStateAction<BillingInfo>>;
  }
>((props, ref) => {
  const {billingInfo, setBillingInfo} = props;

  // Internal error state - no longer passed from parent
  const [errors, setErrors] = useState<
    Partial<Record<keyof BillingInfo, string>>
  >({});

  // Expose validation methods to parent via ref
  useImperativeHandle(ref, () => ({
    validateAll: () => {
      const newErrors: Partial<Record<keyof BillingInfo, string>> = {};

      // Validate all fields
      if (!billingInfo.country) {
        newErrors.country = 'Country is required';
      }

      if (!billingInfo.locality) {
        newErrors.locality = 'City/Locality is required';
      }

      if (!billingInfo.postalCode) {
        newErrors.postalCode = 'Zip/Postal code is required';
      }

      if (!billingInfo.address) {
        newErrors.address = 'Address is required';
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },

    hasErrors: () => {
      return Object.values(errors).some((error) => error !== '');
    },

    getErrors: () => errors,
  }));

  // Real-time validation helpers
  const validateCountry = (value: string) => {
    if (!value) {
      setErrors((prev) => ({...prev, country: ''}));
    } else {
      setErrors((prev) => ({...prev, country: ''}));
    }
  };

  const validateLocality = (value: string) => {
    if (!value) {
      setErrors((prev) => ({...prev, locality: ''}));
    } else {
      setErrors((prev) => ({...prev, locality: ''}));
    }
  };

  const validatePostalCode = (value: string) => {
    if (!value) {
      setErrors((prev) => ({...prev, postalCode: ''}));
    } else {
      setErrors((prev) => ({...prev, postalCode: ''}));
    }
  };

  const validateAddress = (value: string) => {
    if (!value) {
      setErrors((prev) => ({...prev, address: ''}));
    } else {
      setErrors((prev) => ({...prev, address: ''}));
    }
  };

  const handleCountryChange = (country: string) => {
    setBillingInfo({...billingInfo, country});
    validateCountry(country);
  };

  const handleLocalityChange = (value: string) => {
    setBillingInfo({...billingInfo, locality: value});
    validateLocality(value);
  };

  const handlePostalCodeChange = (value: string) => {
    setBillingInfo({...billingInfo, postalCode: value});
    validatePostalCode(value);
  };

  const handleAddressChange = (value: string) => {
    setBillingInfo({...billingInfo, address: value});
    validateAddress(value);
  };

  return (
    <div className="mb-6 w-full">
      <h3 className="text-[16px] font-semibold text-headlineGrayscale mb-4">
        Billing Address
      </h3>

      <div className="space-y-0">
        <CountryDropdown
          value={billingInfo.country}
          onChange={handleCountryChange}
          error={errors.country}
          className={`${
            errors.country ? 'w-full bg-surfaceNegative ' : 'w-full '
          } `}
        />

        <FormField
          form="billing"
          placeholder="Locality"
          value={billingInfo.locality}
          onChange={handleLocalityChange}
          error={errors.locality}
          className={`ps-1 ${
            errors.locality
              ? 'w-full bg-surfaceNegative border-b border-r  border-l text-smoothRed border-pinkNegative'
              : 'w-full border-b border-r border-l border-subtleGrey'
          } `}
        />

        <div className="flex space-x-0">
          <FormField
            form="billing"
            placeholder="Zip/Postal Code"
            value={billingInfo.postalCode}
            onChange={handlePostalCodeChange}
            error={errors.postalCode}
            className={`ps-1 ${
              errors.postalCode
                ? 'w-2/5 bg-surfaceNegative border-b rounded-bl-md border-l text-smoothRed border-pinkNegative'
                : 'w-2/5 border-b rounded-bl-md border-l border-subtleGrey'
            } `}
          />
          <FormField
            form="billing"
            placeholder="Address"
            value={billingInfo.address}
            onChange={handleAddressChange}
            error={errors.address}
            className={`ps-1 ${
              errors.address
                ? 'w-3/5 bg-surfaceNegative border-b border-r rounded-br-md border-l text-smoothRed border-pinkNegative'
                : 'w-3/5 rounded-br-md  border-b border-r border-l border-subtleGrey'
            } `}
          />
        </div>
      </div>
    </div>
  );
});

BillingAddressForm.displayName = 'BillingAddressForm';

export default BillingAddressForm;
